import { Controller } from '@hotwired/stimulus'

// Ученики тренера в конструкторе турниров (account/tournaments/_students).
// Клик по имени вписывает его в первое пустое поле участника своей формы: в
// миксте — только в колонку пола ученика, если пол известен. Кнопка ученика, чьё
// имя уже стоит в составе, гаснет и снова загорается, если имя стёрли.
// Поля перерисовывает tournament-form#rebuild (смена числа игроков), поэтому
// они ищутся при каждом клике, а не запоминаются при подключении.
const FIELDS = {
  any: [
    'user_tournament[player_names][]',
    'user_tournament[men_names][]',
    'user_tournament[women_names][]',
    'name'
  ],
  m: ['user_tournament[men_names][]'],
  w: ['user_tournament[women_names][]']
}

export default class TournamentStudentsController extends Controller {
  static targets = ['chip', 'full']

  connect() {
    this.form = this.element.closest('form') || this.element.parentElement
    this.refresh = this.refresh.bind(this)
    this.form.addEventListener('input', this.refresh)
    this.form.addEventListener('change', this.refresh)
    this.refresh()
  }

  disconnect() {
    this.form.removeEventListener('input', this.refresh)
    this.form.removeEventListener('change', this.refresh)
    clearTimeout(this.fullTimer)
  }

  pick(event) {
    const { name, gender } = event.params
    const input = this.emptyField(gender)
    if (!input) {
      this.showFull()
      return
    }
    input.value = name
    input.dispatchEvent(new Event('input', { bubbles: true }))
    this.syncGender(gender)
    this.rememberAccount(event.params.id)
    this.refresh()
  }

  // Аккаунт ученика для привязки участника. Форма добавления в набор —
  // одно поле user_id; форма турнира — список student_ids[], по одному на
  // выбранного ученика. Сервер привязывает, только если имя участника
  // совпало с именем ученика: переписанное имя привязку не получит.
  rememberAccount(id) {
    if (!id) return

    const single = this.form.querySelector('input[name="user_id"]')
    if (single) {
      single.value = id
      return
    }
    const name = 'user_tournament[student_ids][]'
    const present = [...this.form.querySelectorAll(`input[name="${name}"]`)]
    if (present.some((input) => input.value === String(id))) return

    this.element.append(
      Object.assign(document.createElement('input'), {
        type: 'hidden',
        name,
        value: id
      })
    )
  }

  // Добавление игрока в микст-набор: рядом с именем — выбор пола, и он
  // должен встать по ученику, иначе ученица ушла бы в турнир мужчиной.
  // Пол неизвестен — выбор не трогаем, тренер ставит его сам.
  syncGender(gender) {
    const select = this.form.querySelector('select[name="gender"]')
    if (!select || !['m', 'w'].includes(gender)) return

    select.value = gender
    select.dispatchEvent(new Event('change', { bubbles: true }))
    select
      .closest('[data-controller~="select"]')
      ?.dispatchEvent(new Event('select:refresh'))
  }

  // В миксте ученик с известным полом встаёт только в колонку своего пола:
  // позиция в микст-форме и есть пол, и чужая колонка записала бы его не
  // туда. Нет места — подсказка «увеличьте число игроков». Пол неизвестен
  // или формат не микст — любое пустое поле. Единственное поле (добавление
  // игрока в турнир-набор) заменяется.
  emptyField(gender) {
    const all = this.fields(FIELDS.any)
    if (all.length === 1) return all[0]

    const own = FIELDS[gender] ? this.fields(FIELDS[gender]) : []
    const candidates = own.length ? own : all
    return candidates.find((input) => !input.value.trim())
  }

  fields(names) {
    const selector = names.map((name) => `input[name="${name}"]`).join(', ')
    return [...this.form.querySelectorAll(selector)]
  }

  refresh() {
    const taken = new Set(
      this.fields(FIELDS.any)
        .filter((input) => input.name !== 'name')
        .map((input) => input.value.trim().toLowerCase())
        .filter(Boolean)
    )
    this.chipTargets.forEach((chip) => {
      chip.disabled = taken.has(
        chip.dataset.tournamentStudentsNameParam.toLowerCase()
      )
    })
  }

  showFull() {
    if (!this.hasFullTarget) return
    this.fullTarget.classList.remove('hidden')
    clearTimeout(this.fullTimer)
    this.fullTimer = setTimeout(
      () => this.fullTarget.classList.add('hidden'),
      4000
    )
  }
}
